/**
 * EntityManagerPanel — Convex-backed entity CRUD with drag-and-drop reorder.
 */
import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import {
  Plus,
  Search,
  Trash2,
  Pencil,
  Music,
  Building2,
  Users,
  Calendar,
  Network,
} from 'lucide-react';
import { RelationshipGraphModal } from './RelationshipGraphModal';
import { QuickEventCreator } from './QuickEventCreator';
import { EditEntityModal } from './EditEntityModal';
import { EntityProfile } from './EntityProfile';
import { useConfirm } from '@/hooks/useConfirm';
import { useAppStore } from '@/store/appStore';

const ENTITY_TYPES = [
  { id: 'artist', label: 'Artist', icon: Music, color: 'purple' },
  { id: 'venue', label: 'Venue', icon: Building2, color: 'blue' },
  { id: 'promoter', label: 'Promoter', icon: Users, color: 'amber' },
  { id: 'event', label: 'Event', icon: Calendar, color: 'rose' },
] as const;

const TYPE_COLORS: Record<string, { bg: string; text: string; border: string; badge: string }> = {
  artist: { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/25', badge: 'bg-purple-500/15 text-purple-400 border-purple-500/25' },
  venue: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/25', badge: 'bg-blue-500/15 text-blue-400 border-blue-500/25' },
  promoter: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/25', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/25' },
  event: { bg: 'bg-rose-500/15', text: 'text-rose-400', border: 'border-rose-500/25', badge: 'bg-rose-500/15 text-rose-400 border-rose-500/25' },
};

function SortableEntityItem({
  e,
  meta,
  Icon,
  isEditing,
  editingId,
  editName,
  editDisplayName,
  editDescription,
  setEditName,
  setEditDisplayName,
  setEditDescription,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRemove,
  onView,
}: {
  e: { _id: Id<'entities'>; name: string; displayName?: string; description?: string; entityType: string; [key: string]: any };
  meta: (typeof ENTITY_TYPES)[number] | undefined;
  Icon: React.ElementType;
  isEditing: boolean;
  editingId: Id<'entities'> | null;
  editName: string;
  editDisplayName: string;
  editDescription: string;
  setEditName: (v: string) => void;
  setEditDisplayName: (v: string) => void;
  setEditDescription: (v: string) => void;
  onStartEdit: (entity: any) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onRemove: (id: Id<'entities'>) => void;
  onView: (entity: any) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: e._id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const colors = TYPE_COLORS[e.entityType] || { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', badge: 'bg-muted text-muted-foreground' };
  return (
    <li ref={setNodeRef} style={style} className={`rounded-xl border border-border bg-card p-3 transition-all duration-150 hover:border-opacity-60 ${isDragging ? 'opacity-50 scale-[0.98]' : 'hover:shadow-sm'}`}>
      {isEditing ? (
        <div className="space-y-2 animate-fade-in">
          <input value={editName} onChange={(ev) => setEditName(ev.target.value)} placeholder="Name" className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all" />
          <input value={editDisplayName} onChange={(ev) => setEditDisplayName(ev.target.value)} placeholder="Display name" className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all" />
          <textarea value={editDescription} onChange={(ev) => setEditDescription(ev.target.value)} placeholder="Description" rows={2} className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm resize-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all" />
          <div className="flex gap-2">
            <button onClick={onSaveEdit} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors">Save</button>
            <button onClick={onCancelEdit} className="rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-muted transition-colors">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer group"
            onClick={() => onView(e)}
            {...attributes}
            {...listeners}
          >
            <div className={`flex h-9 w-9 flex-shrink-0 cursor-grab items-center justify-center rounded-lg ${colors.bg} active:cursor-grabbing transition-colors`}>
              <Icon className={`h-4 w-4 ${colors.text}`} />
            </div>
            <div className="min-w-0">
              <div className="font-medium truncate group-hover:text-primary transition-colors">{e.displayName || e.name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${colors.badge} capitalize`}>
                  {e.entityType}
                </span>
                {e.description && <span className="text-xs text-muted-foreground truncate">{e.description}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onStartEdit(e)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onRemove(e._id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export function EntityManagerPanel() {
  const { confirm, dialog } = useConfirm();
  const currentAccountId = useAppStore((s) => s.currentAccountId);
  const accountId = currentAccountId ?? undefined;
  const [search, setSearch] = useState('');
  const [showGraph, setShowGraph] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [viewingEntity, setViewingEntity] = useState<typeof filtered[0] | null>(null);
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState<string>('artist');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createDescription, setCreateDescription] = useState('');

  const entities = useQuery(api.entities.list, { entityType: typeFilter, accountId });
  const createEntity = useMutation(api.entities.create);
  const updateEntity = useMutation(api.entities.update);
  const removeEntity = useMutation(api.entities.remove);
  const reorderEntities = useMutation(api.entities.reorder);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !filtered.length) return;
    const oldIndex = filtered.findIndex((e: any) => e._id === active.id);
    const newIndex = filtered.findIndex((e: any) => e._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(filtered, oldIndex, newIndex);
    reorderEntities({ accountId, ids: reordered.map((e: any) => e._id) });
  };
  const [editingId, setEditingId] = useState<Id<'entities'> | null>(null);
  const [editName, setEditName] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<typeof filtered[0] | null>(null);

  const filtered =
    entities?.filter(
      (e) =>
        !search ||
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        (e.displayName ?? '').toLowerCase().includes(search.toLowerCase())
    ) ?? [];

  const handleCreate = async () => {
    if (!createName.trim()) return;
    try {
      await createEntity({
        accountId,
        entityType: createType,
        name: createName.trim(),
        displayName: createDisplayName.trim() || undefined,
        description: createDescription.trim() || undefined,
      });
      setCreateName('');
      setCreateDisplayName('');
      setCreateDescription('');
      setShowCreateForm(false);
    } catch (err) {
      console.error('Create entity failed:', err);
    }
  };

  const handleRemove = async (id: Id<'entities'>) => {
    const entity = entities?.find((e) => e._id === id);
    const confirmed = await confirm({
      title: 'Delete Entity',
      message: `Are you sure you want to delete "${entity?.displayName || entity?.name || 'this entity'}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await removeEntity({ accountId, id });
    } catch (err) {
      console.error('Remove entity failed:', err);
    }
  };

  const handleStartEdit = (e: typeof filtered[0]) => {
    setEditingId(e._id);
    setEditName(e.name);
    setEditDisplayName(e.displayName ?? '');
    setEditDescription(e.description ?? '');
    setSelectedEntity(e);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    try {
      await updateEntity({
        accountId,
        id: editingId,
        name: editName.trim(),
        displayName: editDisplayName.trim() || undefined,
        description: editDescription.trim() || undefined,
      });
      setEditingId(null);
    } catch (err) {
      console.error('Update entity failed:', err);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <div className="flex-shrink-0 border-b border-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGraph(true)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-2 text-sm hover:bg-muted/50 hover:border-muted-foreground/20 transition-all"
            title="View relationships"
          >
            <Network className="h-4 w-4" />
            <span className="hidden sm:inline">Graph</span>
          </button>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search entities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
            />
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-all active:scale-[0.98] hover:shadow-sm hover:shadow-primary/20"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setTypeFilter(undefined)}
            className={`rounded-full px-2.5 py-1 text-xs font-medium transition-all ${
              !typeFilter
                ? 'bg-primary/15 text-primary shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            All
          </button>
          {ENTITY_TYPES.map(({ id, label, color }) => {
            const colors = TYPE_COLORS[id];
            return (
              <button
                key={id}
                onClick={() => setTypeFilter(typeFilter === id ? undefined : id)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-all ${
                  typeFilter === id
                    ? `${colors.bg} ${colors.text} shadow-sm`
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {showCreateForm && (
          <div className="rounded-lg border border-border bg-card p-3 space-y-2">
            <input
              type="text"
              placeholder="Name *"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Display name"
              value={createDisplayName}
              onChange={(e) => setCreateDisplayName(e.target.value)}
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
            <select
              value={createType}
              onChange={(e) => setCreateType(e.target.value)}
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
            >
              {ENTITY_TYPES.map(({ id, label }) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
            <textarea
              placeholder="Description"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              rows={2}
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={!createName.trim()}
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <QuickEventCreator onCreated={() => {}} />
        {entities === undefined ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in">
            <div className="h-14 w-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
              <Users className="h-7 w-7 text-muted-foreground/40" />
            </div>
            <p className="font-medium text-foreground/80">No entities yet</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-[220px]">
              Add artists, venues, promoters, and events to get started
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filtered.map((e) => e._id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-2">
                {filtered.map((e) => {
                  const meta = ENTITY_TYPES.find((t) => t.id === e.entityType);
                  const Icon = meta?.icon ?? Users;
                  const isEditing = editingId === e._id;
                  return (
                    <SortableEntityItem
                      key={e._id}
                      e={e}
                      meta={meta}
                      Icon={Icon}
                      isEditing={isEditing}
                      editingId={editingId}
                      editName={editName}
                      editDisplayName={editDisplayName}
                      editDescription={editDescription}
                      setEditName={setEditName}
                      setEditDisplayName={setEditDisplayName}
                      setEditDescription={setEditDescription}
                      onStartEdit={handleStartEdit}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={() => setEditingId(null)}
                      onRemove={handleRemove}
                      onView={setViewingEntity}
                    />
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
      <RelationshipGraphModal open={showGraph} onClose={() => setShowGraph(false)} />
      
      <EditEntityModal
        entity={selectedEntity}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedEntity(null);
          setEditingId(null);
        }}
        onSaved={() => {
          setEditingId(null);
          setSelectedEntity(null);
        }}
      />
      
      {viewingEntity && (
        <div className="fixed inset-0 z-50 bg-background">
          <EntityProfile
            entity={viewingEntity}
            onBack={() => setViewingEntity(null)}
            onEdit={() => {
              setSelectedEntity(viewingEntity);
              setShowEditModal(true);
            }}
          />
        </div>
      )}
      
      {dialog}
    </div>
  );
}
